import seedrandom from 'seedrandom';
import { CardRecord } from '../db/indexedDb';

/**
 * 主题随机排序模块
 * 用于实现每日固定种子的主题随机化
 */

// 生成每日种子（基于日期）
export function getDailySeed(): string {
  const today = new Date();
  return `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
}

// 按主题分组（使用第一个 topic 作为主题标识）
function groupByTopic(cards: CardRecord[]): Map<string, CardRecord[]> {
  const groups = new Map<string, CardRecord[]>();
  for (const card of cards) {
    const topic = card.topics?.[0] ?? 'unknown';
    if (!groups.has(topic)) {
      groups.set(topic, []);
    }
    groups.get(topic)!.push(card);
  }
  return groups;
}

// Fisher-Yates 洗牌算法（使用固定种子）
function shuffleArray<T>(array: T[], rng: () => number): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * 口语卡片随机：按 Part 和比例分组后随机主题
 */
export function randomizeSpeakingTopics(
  cards: CardRecord[],
  partRatio: { part1: number; part2: number; part3: number },
  seed: string
): CardRecord[] {
  const rng = seedrandom(seed + '-speaking');

  // 按 Part 分类
  const part1Cards = cards.filter(c => c.topics?.includes('Part 1'));
  const part2Cards = cards.filter(c => c.topics?.includes('Part 2'));
  const part3Cards = cards.filter(c => c.topics?.includes('Part 3'));

  // 每个 Part 内按主题分组
  const part1Topics = groupByTopic(part1Cards);
  const part2Topics = groupByTopic(part2Cards);
  const part3Topics = groupByTopic(part3Cards);

  // 随机打乱主题顺序，但保持每个主题内卡片的原有顺序
  const shuffleTopics = (topicMap: Map<string, CardRecord[]>) => {
    const topicKeys = Array.from(topicMap.keys());
    const shuffledKeys = shuffleArray(topicKeys, rng);
    return shuffledKeys.flatMap(topic => topicMap.get(topic)!);
  };

  const shuffled1 = shuffleTopics(part1Topics);
  const shuffled2 = shuffleTopics(part2Topics);
  const shuffled3 = shuffleTopics(part3Topics);

  // 按比例交织不同 Part 的卡片
  return interleaveParts(shuffled1, shuffled2, shuffled3, partRatio);
}

/**
 * 交织不同 Part 的卡片（按比例）
 * 例如 ratio = {part1: 2, part2: 1, part3: 1} 则按 2:1:1 交替取卡片
 */
function interleaveParts(
  part1: CardRecord[],
  part2: CardRecord[],
  part3: CardRecord[],
  ratio: { part1: number; part2: number; part3: number }
): CardRecord[] {
  const result: CardRecord[] = [];
  let i1 = 0, i2 = 0, i3 = 0;

  // 循环直到所有卡片都被取完
  while (i1 < part1.length || i2 < part2.length || i3 < part3.length) {
    // 按比例取 Part 1 卡片
    for (let i = 0; i < ratio.part1 && i1 < part1.length; i++) {
      result.push(part1[i1++]);
    }
    // 按比例取 Part 2 卡片
    for (let i = 0; i < ratio.part2 && i2 < part2.length; i++) {
      result.push(part2[i2++]);
    }
    // 按比例取 Part 3 卡片
    for (let i = 0; i < ratio.part3 && i3 < part3.length; i++) {
      result.push(part3[i3++]);
    }
  }

  return result;
}

/**
 * 听力/词汇：简单主题随机
 */
export function randomizeSimpleTopics(
  cards: CardRecord[],
  seed: string,
  skillSuffix: string = ''
): CardRecord[] {
  const rng = seedrandom(seed + skillSuffix);
  const topicGroups = groupByTopic(cards);

  // 随机打乱主题顺序
  const topicKeys = Array.from(topicGroups.keys());
  const shuffledKeys = shuffleArray(topicKeys, rng);

  // 按随机后的主题顺序拼接卡片
  return shuffledKeys.flatMap(topic => topicGroups.get(topic)!);
}

/**
 * AI Writing：主题随机
 */
export function randomizeAIWritingTopics(
  cards: CardRecord[],
  seed: string
): CardRecord[] {
  return randomizeSimpleTopics(cards, seed, '-ai-writing');
}
